import { useState } from 'react';
import { toast } from 'react-toastify';

/**
 * Hook for managing camera control operations
 */
const useCameraControl = (cameraStreamRef, backPress) => {
    const [isUploading, setIsUploading] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);

    const handleBackPress = async () => {
        console.log('[useCameraControl] handleBackPress called');
        try {
            // Call stopCamera before navigating back
            if (cameraStreamRef.current) {
                console.log('[useCameraControl] Calling stopCamera');
                cameraStreamRef.current.stopCamera();
                // Wait a bit to ensure camera is fully released
                console.log('[useCameraControl] Waiting 200ms for camera release');
                await new Promise(resolve => setTimeout(resolve, 200));
                console.log('[useCameraControl] Wait complete, navigating back');
            } else {
                console.log('[useCameraControl] No camera ref found');
            }
            backPress();  // Then navigate back
        } catch (error) {
            console.error('[useCameraControl] Error stopping camera:', error);
            toast.error('Error stopping camera. Please try again.');
            backPress(); // Navigate back anyway
        }
    };

    const handleSwitchCamera = () => {
        try {
            if (cameraStreamRef.current) {
                cameraStreamRef.current.switchCamera();
            }
        } catch (error) {
            console.error('Error switching camera:', error);
        }
    };

    const resetCamera = () => {
        setIsUploading(false);
        setPreviewImage(null);
        if (cameraStreamRef.current) {
            try {
                cameraStreamRef.current.resetCamera();
            } catch (error) {
                console.error('Error resetting camera:', error);
            }
        }
    };

    const handleImageSelected = async (input, onImageUpload) => {
        if (input === 'capture') {
            // Trigger camera capture
            try {
                if (cameraStreamRef.current) {
                    const imageBlob = await cameraStreamRef.current.takePhoto();
                    if (imageBlob) {
                        // Convert Blob to File with .jpg extension for camera captures
                        const file = new File([imageBlob], `capture_${Date.now()}.jpg`, { type: 'image/jpeg' });
                        await onImageUpload(file);
                    } else {
                        toast.error('Failed to capture image. Please try again.');
                    }
                }
            } catch (error) {
                console.error('Error capturing image:', error);
                toast.error('Error capturing image. Please try again.');
            }
        } else {
            // Validate file type for gallery uploads
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
            if (!allowedTypes.includes(input.type)) {
                toast.error('Please select a JPG or PNG image');
                return;
            }
            await onImageUpload(input);
        }
    };

    return {
        isUploading,
        setIsUploading,
        previewImage,
        setPreviewImage,
        handleBackPress,
        handleSwitchCamera,
        resetCamera,
        handleImageSelected
    };
};

export default useCameraControl;
